/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"

import { ChangeEvent, useState } from "react"
import { AttributeCluster } from "types/models/menu"
import { ItemAttributeValue } from "types/models/item_attribute"
import { Input, Select, Space } from "@duxton-web/ui"
import { Form } from "grab-ui"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { eventValueToCleanNumericInput } from "utils/form_helper"
import { notShowToPax } from "./AttributeDrawer.utils"
import styles from "./AttributeDrawer.modules.css"

export interface AttributeDrawerDecimalProps extends FormComponentProps {
  attributeCluster: AttributeCluster
  selectedAttributes?: ItemAttributeValue[]
  handleChangeAttributes: (value?: ItemAttributeValue[]) => void
  itemID?: string
  disableFormRule?: boolean
}

export function AttributeDrawerDecimal({
  form,
  attributeCluster,
  selectedAttributes,
  handleChangeAttributes,
  itemID,
  disableFormRule,
}: AttributeDrawerDecimalProps) {
  const textMap = useTranslationsMapTyped({
    infoNotShownToPax: "itemAttributes.info.notShownToPax",
    warningRequired: "itemAttributes.error.required",
    warningNumberExceed: "itemAttributes.error.numberExceed",
  })

  const nameLabel = attributeCluster.nameLocalization || attributeCluster.name
  const unitLabelLocalization =
    attributeCluster.metadata.decimalMetadata?.unitLocalization
  const unitLabel = attributeCluster.metadata.decimalMetadata?.units
  const minLimit = attributeCluster.metadata.decimalMetadata?.min.value || 0
  const maxLimit = attributeCluster.metadata.decimalMetadata?.max.value || 10000
  const decimalLimit =
    attributeCluster.metadata?.decimalMetadata?.numberOfDecimalPlaces.value || 0

  const unitListLocalization = unitLabelLocalization?.split(",")
  const unitList = unitLabel?.split(",")

  const { getFieldDecorator, setFieldsValue } = form

  const initialValue = selectedAttributes?.find(
    (key: ItemAttributeValue) => key.attributeClusterID === attributeCluster.id,
  )

  const [unitLocalizationValue, setUnitLocalizationValue] = useState(
    initialValue?.metadata?.unitLocalization || unitListLocalization?.[0],
  )

  const [unitValue, setUnitValue] = useState(
    initialValue?.metadata?.unit || unitList?.[0],
  )
  const [decimalValue, setDecimalValue] = useState(initialValue?.value)

  const handleTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newDecimalValue = eventValueToCleanNumericInput(event, decimalLimit)
    const attributeSelected: ItemAttributeValue = {
      itemID,
      status: attributeCluster?.status || "Enabled",
      value: newDecimalValue,
      attributeClusterID: attributeCluster?.id,
      metadata: {
        unit: unitValue,
      },
    }
    let finalValue = selectedAttributes?.filter(
      key => key.attributeClusterID !== attributeCluster?.id,
    )
    if (newDecimalValue.length) {
      finalValue = finalValue
        ? finalValue.concat(attributeSelected)
        : [attributeSelected]
    }

    setDecimalValue(newDecimalValue)
    handleChangeAttributes(finalValue)
  }

  const handleUnitChange = (value: string) => {
    const arrayValue = value.split(",")
    let finalValue = selectedAttributes?.filter(
      key => key.attributeClusterID !== attributeCluster?.id,
    )
    if (decimalValue) {
      const attributeSelected: ItemAttributeValue = {
        itemID,
        status: attributeCluster?.status || "Enabled",
        value: decimalValue,
        attributeClusterID: attributeCluster?.id,
        metadata: {
          unit: arrayValue?.[0],
        },
      }
      finalValue = finalValue
        ? finalValue.concat(attributeSelected)
        : [attributeSelected]
    }

    setFieldsValue({
      [`attributeForm.${nameLabel}.unitLocalization`]: arrayValue?.[1],
    })

    setUnitLocalizationValue(value)
    setUnitValue(arrayValue?.[0])
    handleChangeAttributes(finalValue)
  }

  const handleLimitValue = (rule: any, value: string, callback: any) => {
    if (value && (Number(value) < minLimit || Number(value) > maxLimit)) {
      callback(rule)
    } else {
      callback()
    }
  }

  return (
    <div data-testid="attribute-drawer-decimal" className={styles.formWrapper}>
      <Form.Item label={nameLabel}>
        <Space.Compact block>
          {getFieldDecorator(`attributeForm.${nameLabel}.value`, {
            initialValue: decimalValue,
            getValueFromEvent: (e: ChangeEvent<HTMLInputElement>) =>
              eventValueToCleanNumericInput(e, decimalLimit),
            rules: disableFormRule
              ? undefined
              : [
                  {
                    required:
                      attributeCluster?.attributeClusterOption?.isMandatory,
                    message: textMap.warningRequired,
                  },
                  {
                    validator: handleLimitValue,
                    message: textMap.warningNumberExceed
                      .replace("<min>", String(minLimit))
                      .replace("<max>", String(maxLimit)),
                  },
                ],
          })(
            <Input
              className={styles.selectInputDecimal}
              autoComplete="off"
              data-testid="input-attribute-decimal"
              placeholder="Please Type Number"
              onChange={handleTextChange}
            />,
          )}
          <Select
            className={styles.selectUnitDecimal}
            defaultValue={unitLocalizationValue}
            onChange={handleUnitChange}
          >
            {unitListLocalization?.map((value: string, index: number) => (
              <Select.Option
                key={value}
                value={`${unitList?.[index]},${value}`}
              >
                {value}
              </Select.Option>
            ))}
          </Select>
          {getFieldDecorator(`attributeForm.${nameLabel}.unitLocalization`, {
            initialValue: unitLocalizationValue,
          })(<input type="hidden" />)}
        </Space.Compact>
      </Form.Item>
      {!attributeCluster.attributeClusterOption.isShownToPax &&
        notShowToPax(textMap.infoNotShownToPax)}
    </div>
  )
}
